import { Button } from "@mui/material";
import { useState } from "react";
import ResaleListingForm from "./form";
import {
  FinishingTypes,
  PaymentTerms,
  Track,
} from "@/modules/property-tracks/types";
import { ListingType, PriceRoles } from "@/modules/property-tracks/dtos";
import { useAddListing } from "@/modules/property-tracks/hooks/listings/add-listing.hook";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import {
  ResaleListingSubmitFormFields,
  clearedCountToNull,
} from "@/modules/property-tracks/utils";

export default function AddResaleListing({
  track: { property, ...track },
}: {
  track: Track;
}) {
  const [open, setOpen] = useState(false);

  const mutation = useAddListing();

  const handleSubmit = (body: ResaleListingSubmitFormFields) => {
    const mergedDoucments = [
      ...(body.resaleDocumentsDetails?.floorPlanUids ?? []),
      ...(body.resaleDocumentsDetails?.mediaUids ?? []),
    ];
    mutation.mutate(
      {
        listingType: ListingType.RESALE,
        trackUid: track.uid,
        publish: body.publish ?? false,
        documentUids: mergedDoucments.length > 0 ? mergedDoucments : undefined,
        propertyPrice: {
          priceRole: PriceRoles.LISTING,
          paymentType: body.propertyPriceDetails?.paymentType,
          ...(body.propertyPriceDetails?.paymentType === "cash"
            ? {
                price: body.propertyPriceDetails?.price,
              }
            : {
                price:
                  (Number(body.propertyPriceDetails?.downPayment) || 0) +
                  (Number(body.propertyPriceDetails?.remainingInstallments) ||
                    0),
                downPayment: body.propertyPriceDetails?.downPayment,
                remainingInstallments:
                  body.propertyPriceDetails?.remainingInstallments,
                maintenance: body.propertyPriceDetails?.maintenance,
                clubhouse: body.propertyPriceDetails?.clubhouse,
                parking: body.propertyPriceDetails?.parking,
                durationRemainingMonths:
                  body.propertyPriceDetails?.durationRemainingMonths,
                durationRemainingYears:
                  body.propertyPriceDetails?.durationRemainingYears,
                installmentFrequency:
                  body.propertyPriceDetails?.installmentFrequency?.value,
              }),
        },
        property: {
          compoundId: body.propertyDetails?.compound.value,
          propertyTypeId: body.propertyDetails?.propertyType.value,
          unitId: body.propertyDetails?.unitId,
          // The multi-step submit passes raw form values, so a cleared input arrives as "" and
          // the backend coerces it to 0. Null is what clears the column.
          numberOfBedrooms: clearedCountToNull(
            body.propertyDetails?.numberOfBedrooms
          ),
          numberOfBathrooms: clearedCountToNull(
            body.propertyDetails?.numberOfBathrooms
          ),
          unitArea: clearedCountToNull(body.propertyDetails?.unitArea),
          phase: body.propertyDetails?.phase,
          landArea: clearedCountToNull(body.propertyDetails?.landArea),
          gardenArea: clearedCountToNull(body.propertyDetails?.gardenArea),
          roofArea: clearedCountToNull(body.propertyDetails?.roofArea),
          floorNumber: clearedCountToNull(body.propertyDetails?.floorNumber),
          numberOfFloors: clearedCountToNull(body.propertyDetails?.numberOfFloors),
          deliveryDate: body.propertyDetails?.readyToMove
            ? undefined
            : body.propertyDetails?.deliveryDate,
          lastInstallmentDate: body.propertyDetails?.lastInstallmentDate,
          readyToMove: body.propertyDetails?.readyToMove,
          description: body.propertyDetails?.description ?? undefined,
          finishing: body.propertyDetails?.finishing?.value,
          websiteListingId: clearedCountToNull(
            body.propertyDetails?.websiteListingId
          ),
          amenityIds: [...(body.propertyDetails?.amenityIds?.values() ?? [])],
        },
        descriptionAr:
          body.listingDescriptionDetails?.descriptionAr ?? undefined,
        descriptionEn:
          body.listingDescriptionDetails?.descriptionEn ?? undefined,
      },
      {
        onSuccess: () => {
          showToast({
            type: ToastType.SUCCESS,
            message: "Listing added successfully",
            toastId: TOAST_IDS.ADD_LISTING_SUCCESS,
          });
          setOpen(false);
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_LISTING_ERROR,
          });
        },
      },
    );
  };
  return (
    <>
      <Button
        variant="contained"
        sx={(t) => ({
          textTransform: "none",
          color: t.nawy.colors.semantic.text.onColor,
          backgroundColor: t.nawy.colors.semantic.layer.accent,
        })}
        onClick={() => setOpen(true)}
      >
        Add Listing Details
      </Button>
      <ResaleListingForm
        open={open}
        setOpen={setOpen}
        header={"Add Listing"}
        isSubmitting={mutation.isPending}
        onSubmit={handleSubmit}
        trackUid={track.uid}
        defaultValues={{
          propertyDetails: {
            compound: property.compound
              ? {
                  label: property.compound?.name,
                  value: property.compound?.id,
                }
              : undefined,
            propertyType: property.propertyType
              ? {
                  label: property.propertyType?.name,
                  value: property.propertyType?.id,
                }
              : undefined,
            unitId: property.unitId ?? undefined,
            numberOfBedrooms: property.numberOfBedrooms ?? undefined,
            numberOfBathrooms: property.numberOfBathrooms ?? undefined,
            unitArea: property.unitArea ?? undefined,
            phase: property.phase ?? undefined,
            landArea: property.landArea ?? undefined,
            gardenArea: property.gardenArea ?? undefined,
            roofArea: property.roofArea ?? undefined,
            floorNumber: property.floorNumber ?? undefined,
            numberOfFloors: property.numberOfFloors ?? undefined,
            deliveryDate: property.deliveryDate
              ? new Date(property.deliveryDate)
              : undefined,
            lastInstallmentDate: property.lastInstallmentDate
              ? new Date(property.lastInstallmentDate)
              : undefined,
            readyToMove: property.readyToMove ?? undefined,
            description: property.description ?? undefined,
            websiteListingId: property.websiteListingId ?? undefined,
            finishing: property.finishing
              ? {
                  label: FinishingTypes[property.finishing],
                  value: property.finishing,
                }
              : undefined,
            amenityIds: new Set(
              property.propertyAmenity?.map((item) => item.amenity.id) || [],
            ),
          },
          propertyPriceDetails: property.propertyListingPrice
            ? {
                price: property.propertyListingPrice.price,
                paymentType: property.propertyListingPrice.paymentType,
                downPayment: property.propertyListingPrice.downPayment,
                remainingInstallments:
                  property.propertyListingPrice.remainingInstallments,
                clubhouse: property.propertyListingPrice?.clubhouse,
                parking: property.propertyListingPrice?.parking,
                maintenance: property.propertyListingPrice?.maintenance,
                durationRemainingMonths:
                  property.propertyListingPrice.durationRemainingMonths,
                durationRemainingYears:
                  property.propertyListingPrice.durationRemainingYears,
                installmentFrequency: property.propertyListingPrice
                  ?.installmentFrequency
                  ? {
                      label:
                        PaymentTerms[
                          property.propertyListingPrice.installmentFrequency
                        ],
                      value: property.propertyListingPrice.installmentFrequency,
                    }
                  : undefined,
              }
            : undefined,
        }}
      />
    </>
  );
}
